import { useState } from "react";
import type { CartBooking } from "../../types";
import type { ApiClient } from "../../api/client";
import { useCartStore } from "../../store/cart-store";
import { sendTrackingEvent } from "../../lib/tracking";
import {
  formatCartEventDate,
  computeValidationDate,
  formatDisplayLongDate,
} from "../../utils/booking-utils";
import { CountdownTimer } from "../CountdownTimer";
import { useTranslations } from "../../i18n";

type CartItemListProps = {
  apiClient: ApiClient;
  cartId: string;
};

export function CartItemList({ apiClient, cartId }: CartItemListProps) {
  const { cart, removeItem, setCurrentStep } = useCartStore();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { locale, t } = useTranslations();

  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    }).format(priceInCents / 100);
  };

  if (!cart || cart.bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-8">
        <div className="flex size-16 items-center justify-center rounded-full bg-gray-100">
          <svg
            className="size-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
            />
          </svg>
        </div>
        <p className="text-sm text-gray-500">{t.cart.emptyCart}</p>
      </div>
    );
  }

  const handleRemove = async (bookingId: string) => {
    // Tracking remove_from_cart avant suppression
    const itemToRemove = cart.bookings.find((b) => b.id === bookingId);
    if (itemToRemove) {
      sendTrackingEvent(
        "remove_from_cart",
        null,
        {
          currency: "EUR",
          value: itemToRemove.itemsTotal / 100,
          cart_id: cartId,
          items: itemToRemove.bookingItems.map((bi) => ({
            item_id: itemToRemove.id,
            item_name: itemToRemove.productTitle,
            item_category: itemToRemove.optionName,
            item_category2: bi.unitName,
            item_variant: itemToRemove.eventDate || undefined,
            price: ((bi.promotionPrice ?? 0) > 0 ? (bi.promotionPrice ?? 0) : (bi.price ?? 0)) / 100,
            quantity: bi.quantity,
          })),
        }
      );
    }

    setRemovingId(bookingId);
    try {
      await apiClient.removeFromCart(cartId, bookingId);
      removeItem(bookingId);
    } catch (e) {
      console.error("Error removing item from cart:", e);
    } finally {
      setRemovingId(null);
    }
  };

  const activeBookingsCount = cart.bookings.filter(
    (b) => b.status === "IN_CART",
  ).length;
  const hasExpiredBookings = cart.bookings.some(
    (b) => b.status === "ABANDONED",
  );

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">
          {t.cart.yourCart.replace("{count}", String(activeBookingsCount)).replace("{unit}", activeBookingsCount > 1 ? t.cart.articles : t.cart.article)}
        </h2>
      </div>

      {/* Message si des offres ont expiré */}
      {hasExpiredBookings && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
          <p className="text-sm text-orange-700">
            {t.cart.expiredOffers}
          </p>
        </div>
      )}

      {/* Liste des articles */}
      <div className="space-y-3">
        {cart.bookings.map((item: CartBooking) => {
          const isExpired = item.status === "ABANDONED";
          return (
          <div
            key={item.id}
            className={`rounded-xl border p-4 transition-all ${
              isExpired
                ? "border-orange-200 bg-orange-50/50 opacity-75"
                : "border-gray-200 bg-white hover:shadow-sm"
            }`}
          >
            <div className="flex gap-3">
              {/* Image */}
              {item.image && (
                <img
                  src={process.env.PUBLIC_STORAGE_URL + item.image}
                  alt={item.productTitle}
                  className={`size-16 sm:size-20 rounded-lg object-cover flex-shrink-0 ${
                    isExpired ? "grayscale" : ""
                  }`}
                />
              )}

              {/* Détails */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base text-gray-900 break-words">
                      {item.productTitle}
                    </h3>
                    <p className="text-sm text-brand-700 mt-0.5">
                      {item.optionName}
                    </p>
                  </div>
                  <span className={`text-sm sm:text-base font-bold whitespace-nowrap ${
                    isExpired ? "text-orange-600 line-through" : "text-gray-900"
                  }`}>
                    {formatPrice(item.itemsTotal)}
                  </span>
                </div>

                {/* Date + badge cadeau + badge expiré + countdown */}
                <div className="mt-1 space-y-1">
                  {isExpired && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 border border-orange-200">
                      <svg
                        className="size-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                      {t.cart.expiredOffer}
                    </span>
                  )}
                  {!isExpired && item.createdAt && (
                    <CountdownTimer
                      createdAt={item.createdAt}
                      onExpired={() => {
                        useCartStore.setState((state) => {
                          if (!state.cart) return {};
                          const newBookings = state.cart.bookings.map((b) =>
                            b.id === item.id ? { ...b, status: "ABANDONED" as const } : b,
                          );
                          const activeBookings = newBookings.filter((b) => b.status === "IN_CART");
                          return {
                            cart: {
                              ...state.cart,
                              bookings: newBookings,
                              totalAmount: activeBookings.reduce((acc, b) => acc + b.itemsTotal, 0),
                              itemsCount: activeBookings.length,
                            },
                          };
                        });
                      }}
                    />
                  )}
                  {item.eventDate && (
                    <p className={`text-xs ${isExpired ? "text-orange-600 line-through" : "text-gray-500"}`}>
                      {formatCartEventDate(item.eventDate, locale, t.cartBadge.at)}
                    </p>
                  )}
                  {item.isGift && (
                    <div className="space-y-1">
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 border border-purple-100">
                        <svg
                          className="size-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
                          />
                        </svg>
                        {t.cart.giftCard}
                      </span>
                      <p className="text-xs text-gray-500">
                        {t.cart.validUntil}{" "}
                        {formatDisplayLongDate(
                          computeValidationDate(
                            new Date(),
                            item.giftValidationType || "ONE_YEAR",
                          ),
                          locale,
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Détail des billets */}
                {item.bookingItems.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {item.bookingItems.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="flex items-center justify-between text-xs text-gray-500"
                      >
                        <span>
                          {ticket.quantity}x {ticket.unitName}
                        </span>
                        <span>
                          {formatPrice(
                            (ticket.promotionPrice || ticket.price) *
                              ticket.quantity,
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bouton retirer/supprimer */}
                <div className={`mt-2 pt-2 flex justify-end ${isExpired ? "border-t border-orange-200" : "border-t border-gray-100"}`}>
                  <button
                    type="button"
                    onClick={() => handleRemove(item.id)}
                    disabled={removingId === item.id}
                    className={`inline-flex items-center gap-1 text-xs transition-colors disabled:opacity-50 ${
                      isExpired
                        ? "text-orange-600 hover:text-orange-800"
                        : "text-red-500 hover:text-red-700"
                    }`}
                  >
                    {removingId === item.id ? (
                      <svg
                        className="animate-spin size-3.5"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="size-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                        />
                      </svg>
                    )}
                    {isExpired ? t.common.delete : t.common.remove}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* Total + bouton checkout */}
      <div className="rounded-xl bg-gray-50 p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <span className="text-base font-semibold text-gray-900">Total</span>
          <span className="text-xl font-bold text-brand-700">
            {formatPrice(cart.totalAmount)}
          </span>
        </div>

        <button
          type="button"
          onClick={() => {
            // Tracking begin_checkout
            const activeItems = cart.bookings.filter((b) => b.status === "IN_CART");
            const items = activeItems.flatMap((item) =>
              item.bookingItems.map((bi) => ({
                item_id: item.id,
                item_name: item.productTitle,
                item_category: item.optionName,
                item_category2: bi.unitName,
                item_variant: item.eventDate || undefined,
                price: ((bi.promotionPrice ?? 0) > 0 ? (bi.promotionPrice ?? 0) : (bi.price ?? 0)) / 100,
                quantity: bi.quantity,
              }))
            );
            sendTrackingEvent(
              "begin_checkout",
              "cart_checkout", 
              {
                currency: "EUR",
                value: cart.totalAmount / 100,
                cart_id: cartId,
                items,
              }
            );
            setCurrentStep("info");
          }}
          disabled={activeBookingsCount === 0}
          className={`w-full rounded-xl px-4 py-3.5 text-base font-bold text-white shadow-lg transition-all ${
            activeBookingsCount === 0
              ? "bg-gray-400 cursor-not-allowed shadow-gray-200"
              : "bg-brand-600 shadow-brand-200 hover:bg-brand-700 hover:shadow-xl hover:-translate-y-0.5"
          }`}
        >
          {activeBookingsCount === 0 ? t.common.emptyCart : t.common.proceedToPayment}
        </button>
      </div>
    </div>
  );
}
